import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { shareQuery } from '@/api/queries'
import type { OverallStatus } from '@/api/types'
import { StatusIcon, UptimeDays } from '@/components/UptimeDays'
import { Card, Loading } from '@/components/ui'
import { formatMs, formatPercent, formatRelative, intervalLabel, overallStatusLabel } from '@/lib/format'

/**
 * Sdileny (read-only) detail sluzby: /share/{token}.
 *
 * Zamerne mimo layout `_app`: zadna hlavicka, zadny odkaz "zpet", zadne
 * tlacitko upravit ani smazat. Kdo dostal odkaz, ma videt stav sluzby —
 * a nic vic. Data jdou z verejneho API, takze stranka funguje bez
 * prihlaseni i bez uctu.
 *
 * Odkaz jde kdykoli zrusit; pak tenhle pohled vraci stejnou hlasku jako
 * neexistujici token, at se z odpovedi neda poznat, ktere tokeny existovaly.
 */
export const Route = createFileRoute('/share/$token')({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(shareQuery(params.token)),
  component: SharePage,
  errorComponent: () => (
    <Shell>
      <Card className="text-center text-sm text-slate-500">
        Tenhle sdileny odkaz neexistuje nebo byl zrusen.
      </Card>
    </Shell>
  ),
})

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-5xl space-y-6">{children}</div>
    </div>
  )
}

const bannerStyles: Record<OverallStatus, string> = {
  OPERATIONAL: 'bg-green-50 text-green-800 ring-green-600/20',
  DEGRADED: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  OUTAGE: 'bg-red-50 text-red-800 ring-red-600/20',
  UNKNOWN: 'bg-slate-50 text-slate-600 ring-slate-500/20',
}

function SharePage() {
  const { token } = Route.useParams()
  const { data, isLoading } = useQuery(shareQuery(token))

  if (isLoading || !data) {
    return (
      <Shell>
        <Loading />
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">{data.name}</h1>
        <p className="text-sm text-slate-500">Dostupnost za poslednich {data.windowDays} dnu</p>
      </div>
      {data.description && <p className="text-sm text-slate-500">{data.description}</p>}

      <div className={`rounded-lg px-4 py-3 text-sm font-medium ring-1 ring-inset ${bannerStyles[data.overallStatus]}`}>
        {overallStatusLabel(data.overallStatus)}
      </div>

      {data.targets.length === 0 ? (
        <Card className="text-center text-sm text-slate-500">Tahle sluzba zatim nema zadny cil.</Card>
      ) : (
        <>
          {/* Stejne pruhy jako v administraci, jen bez prokliku na detail —
              ten by vedl do chranene casti, kam ctenar odkazu nema pristup. */}
          <Card>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="text-sm font-medium text-slate-700">Dostupnost za {data.windowDays} dnu</h2>
              <span className="text-xs text-slate-400">dny bez mereni jsou sede</span>
            </div>
            <div className="space-y-3">
              {data.targets.map((target) => (
                <div key={target.id} className="flex items-center gap-3">
                  <div className="flex w-40 shrink-0 items-center gap-2 sm:w-52">
                    <StatusIcon status={target.status} />
                    <span className="truncate text-sm font-medium text-slate-700" title={target.name}>
                      {target.name}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <UptimeDays days={target.daily} />
                  </div>
                  <span className="w-20 shrink-0 text-right text-sm font-medium tabular-nums text-slate-600">
                    {target.uptimeWindow === null ? '—' : formatPercent(target.uptimeWindow)}
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-1">
                <span className="w-40 shrink-0 sm:w-52" />
                <div className="flex min-w-0 flex-1 items-center gap-2 text-xs text-slate-400">
                  <span className="whitespace-nowrap">pred {data.windowDays} dny</span>
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="whitespace-nowrap">dnes</span>
                </div>
                <span className="w-20 shrink-0" />
              </div>
            </div>
          </Card>

          <Card className="overflow-x-auto p-0">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Stav</th>
                  <th className="px-4 py-3">Cil</th>
                  <th className="px-4 py-3">Interval</th>
                  <th className="px-4 py-3 text-right">Odezva</th>
                  <th className="px-4 py-3 text-right">24h SLA</th>
                  <th className="px-4 py-3">Mereno</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.targets.map((target) => (
                  <tr key={target.id}>
                    <td className="px-4 py-3">
                      <StatusIcon status={target.status} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{target.name}</p>
                      <p className="max-w-[280px] truncate text-xs text-slate-400">{target.url}</p>
                      {!target.isActive && <span className="text-xs text-amber-600">mereni vypnuto</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {target.method} · {intervalLabel(target.intervalSeconds)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatMs(target.lastResponseTimeMs)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatPercent(target.uptime24h)}</td>
                    <td className="px-4 py-3 text-slate-500">{formatRelative(target.lastCheckedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <p className="text-center text-xs text-slate-400">
        Aktualizovano {formatRelative(data.generatedAt)} · monitoring.individualstartup.cz
      </p>
    </Shell>
  )
}
